import React, { useState } from 'react';
import { siteData } from '../data';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function ContactForm() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-16 bg-blue-950 text-white overflow-hidden">
      {/* Triangle Watermark */}
      <div className="absolute bottom-0 left-0 -mb-24 -ml-24 w-96 h-96 opacity-5 pointer-events-none">
        <svg viewBox="0 0 100 100" fill="currentColor" className="text-white w-full h-full transform -rotate-12">
          <polygon points="0,100 100,100 0,0" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid lg:grid-cols-2 gap-12 lg:gap-16"
        >
          <div>
            <h2 className="text-2xl lg:text-5xl font-bold text-white mb-6">Skal vi pudse dine vinduer?</h2>
            <p className="text-lg lg:text-xl text-white mb-12">
              Kontakt os i dag for et uforpligtende tilbud eller hvis du har spørgsmål til vores services. Du er også meget velkommen til at sende os en SMS.
            </p>
            
            <div className="space-y-8">
              <div className="flex items-center space-x-4 lg:space-x-6">
                <div className="h-12 w-12 lg:h-14 lg:w-14 text-white rounded-none flex items-center justify-center flex-shrink-0">
                  <Phone size={24} className="lg:w-7 lg:h-7" />
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-white font-medium uppercase tracking-wider">Ring til os</p>
                  <a href={`tel:${siteData.contact.phone}`} className="text-lg lg:text-xl font-bold text-white whitespace-nowrap hover:text-blue-200 transition-colors block">
                    {siteData.contact.phone}
                  </a>
                </div>
              </div>
              
              <div className="flex items-center space-x-4 lg:space-x-6">
                <div className="h-12 w-12 lg:h-14 lg:w-14 text-white rounded-none flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} className="lg:w-7 lg:h-7" />
                </div>
                <div>
                  <p className="text-xs lg:text-sm text-white font-medium uppercase tracking-wider">Vi dækker</p>
                  <p className="text-lg lg:text-xl font-bold text-white">Storkøbenhavn</p>
                </div>
              </div>
            </div>
          </div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="p-4 sm:p-8 lg:p-12 rounded-none bg-white/5 border border-white/10"
          >
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="h-20 w-20 bg-blue-600/20 text-blue-400 rounded-lg flex items-center justify-center mb-6 border border-blue-500/30">
                  <CheckCircle size={48} />
                </div>
                <h3 className="text-3xl font-bold mb-4 text-white">Tak for din besked!</h3>
                <p className="text-lg text-blue-100">Vi vender tilbage til dig hurtigst muligt.</p>
                <button 
                  onClick={() => setStatus(null)}
                  className="mt-8 text-white font-bold hover:text-blue-300 transition-colors underline decoration-blue-500 underline-offset-4"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - HIDDEN FROM HUMANS */}
                <div className="sr-only absolute opacity-0 -z-10 select-none pointer-events-none">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-blue-100 mb-2">Navn</label>
                    <input 
                      type="text" 
                      name="name" 
                      className="w-full px-6 py-4 rounded-none border-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 placeholder:text-gray-400 transition-all outline-none"
                      placeholder="Dit navn"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-blue-100 mb-2">Telefon <span className="text-red-400">*</span></label>
                    <input 
                      type="tel" 
                      name="phone" 
                      required 
                      className="w-full px-6 py-4 rounded-none border-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 placeholder:text-gray-400 transition-all outline-none"
                      placeholder="Dit nummer"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-blue-100 mb-2">Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    className="w-full px-6 py-4 rounded-none border-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 placeholder:text-gray-400 transition-all outline-none"
                    placeholder="din@mail.dk"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-blue-100 mb-2">Besked</label>
                  <textarea 
                    name="message" 
                    rows="4" 
                    className="w-full px-6 py-4 rounded-none border-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 placeholder:text-gray-400 transition-all outline-none"
                    placeholder="Hvad kan vi hjælpe dig med?"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-white text-blue-950 px-8 py-5 md:px-5 md:py-3.5 md:text-base lg:px-6 lg:py-4 lg:text-base xl:px-8 xl:py-5 xl:text-lg rounded-none font-bold hover:bg-blue-50 transition-all flex items-center justify-center disabled:opacity-50 group"
                >
                  {loading ? 'Sender...' : (
                    <>
                      Send besked
                      <Send className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={20} />
                    </>
                  )}
                </button>
                
                {status === 'error' && (
                  <p className="text-red-400 text-center font-medium mt-4">Der skete en fejl. Prøv igen eller ring til os.</p>
                )}
              </form>
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
